import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import useSnackbarStore from '@store/snackbar-store/snackbar.store.ts';
import { SnackbarType } from '@models/app-snackbar.model.ts';
import { CallStateEnum } from '@models/call-state.model.ts';
import { withCallState } from '@shared/store-features/call-state-feature.ts';
import axios from 'axios';
import {
  UnitActionParams,
  UNITS_INITIAL_STATE,
  UnitsState
} from '@modules/departments-and-units/pages/units/store/units.model.ts';
import {
  createUnit,
  editUnit,
  changeStatus,
  getAssignedAdmin
} from '@api/departments-and-units/units.api.ts';
import { UnitReqModel } from '@models/departments-and-units/units.model.ts';

const { showSnackbar } = useSnackbarStore.getState();

const useUnitsStore = create<UnitsState>()(
  devtools(
    (set, getState) => ({
      ...UNITS_INITIAL_STATE,
      ...withCallState(getState),

      getAssignedAdminList: async () => {
        try {
          const assignedAdminData = await getAssignedAdmin();

          const assignedAdminList = assignedAdminData.map((item) => ({
            id: item.id,
            title: `${item.fullName}, email: ${item.email}`,
            value: item.fullName
          }));

          set({
            assignedAdmin: assignedAdminData,
            assignedAdminList
          });
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      createUnit: async ({ data, state, handleClose }: UnitActionParams) => {
        set({ callState: CallStateEnum.Loading });
        try {
          const res = await createUnit(data as UnitReqModel);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          if (state) state?.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar('snackbar_success_untis_text', SnackbarType.Success);
          return res;
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
          return null;
        }
      },

      editUnit: async ({ data, id, state, handleClose }: UnitActionParams) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await editUnit(data as UnitReqModel, id as number);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state?.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar('snackbar_update_untis_text', SnackbarType.Success);
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      changeStatus: async ({
        id,
        isActive,
        state,
        handleClose
      }: UnitActionParams) => {
        set({ callState: CallStateEnum.Loading });
        try {
          const data = await changeStatus(id as number, isActive as boolean);

          const snackbarText = data?.isActive
            ? 'snackbar_activate_untis_text'
            : 'snackbar_deactivate_untis_text';

          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state?.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar(snackbarText, SnackbarType.Success);
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      }
    }),
    { name: 'UnitsStore' }
  )
);

export default useUnitsStore;
